import type { DrawerSlotHandle } from '@opengov/psp-contract';

/** Hard priority band. `managed` (shell-owned) slots always render to the left
 *  of `remote` (module-owned) slots. Remote modules cannot request `managed`. */
export type DrawerSlotTier = 'managed' | 'remote';

/** A registered drawer slot as seen by the rendering layer. */
export interface DrawerSlotRecord {
  readonly id: string;
  readonly tier: DrawerSlotTier;
  readonly order: number;
  readonly registrationSeq: number;
  readonly open: boolean;
  readonly element: HTMLDivElement;
}

interface RegisterOptions {
  tier: DrawerSlotTier;
  order?: number;
  /** Pre-made element to adopt as the slot's node. When omitted, the store
   *  creates one. The hook supplies its own so `portalTargetRef` is populated
   *  on the first render, before registration runs in a mount effect. */
  element?: HTMLDivElement;
  /** Imperative "actually close my drawer" hook. The store only tracks the
   *  open flag for layout; the drawer's real open state lives in its owner
   *  (e.g. the OG Assist SDK). This lets the shell ask the owner to close so
   *  two right-side panels never claim space at once. */
  onRequestClose?: () => void;
}

const TIER_RANK: Record<DrawerSlotTier, number> = { managed: 0, remote: 1 };

function compareSlots(a: DrawerSlotRecord, b: DrawerSlotRecord): number {
  return (
    TIER_RANK[a.tier] - TIER_RANK[b.tier] ||
    a.order - b.order ||
    a.registrationSeq - b.registrationSeq
  );
}

/**
 * External store holding the ordered set of drawer slots. Lives as a module
 * singleton (see `drawerSlotStore`) so the imperative shell-bridge API can
 * register slots from outside the shell's React tree, mirroring the
 * `headerActionSlotRef` pattern. The React layer reads it via
 * `useSyncExternalStore`.
 */
export class DrawerSlotStore {
  private slots: DrawerSlotRecord[] = [];
  private listeners = new Set<() => void>();
  /** Imperative close hooks, keyed by slot id. Kept off the rendered snapshot
   *  so it stays pure data. */
  private closeHooks = new Map<string, () => void>();
  private seq = 0;

  register({ tier, order = 0, element, onRequestClose }: RegisterOptions): DrawerSlotHandle {
    const id = `drawer-slot-${this.seq}`;
    const node = element ?? document.createElement('div');
    const record: DrawerSlotRecord = {
      id,
      tier,
      order,
      registrationSeq: this.seq,
      open: false,
      element: node,
    };
    this.seq += 1;

    if (onRequestClose) this.closeHooks.set(id, onRequestClose);

    this.warnOnOrderCollision(record);
    this.slots = [...this.slots, record].sort(compareSlots);
    this.emit();

    return {
      element: node,
      setOpen: (open: boolean) => this.setOpen(id, open),
      release: () => this.release(id),
    };
  }

  setOpen(id: string, open: boolean): void {
    const slot = this.slots.find((s) => s.id === id);
    if (!slot || slot.open === open) return;
    this.slots = this.slots.map((s) => (s.id === id ? { ...s, open } : s));
    this.emit();
  }

  release(id: string): void {
    this.closeHooks.delete(id);
    const next = this.slots.filter((s) => s.id !== id);
    if (next.length === this.slots.length) return;
    this.slots = next;
    this.emit();
  }

  /**
   * Ask every open `managed` (shell-owned) drawer to close — both invoking its
   * imperative close hook (so the owner's real drawer closes) and clearing the
   * layout open flag. Used to keep shell right-side panels mutually exclusive:
   * opening one (e.g. Command Center's Customize drawer) yields the space held
   * by another (e.g. OG Assist). Leaves `remote` module drawers untouched.
   */
  closeManagedDrawers(): void {
    let changed = false;
    this.slots = this.slots.map((s) => {
      if (s.tier !== 'managed' || !s.open) return s;
      // Isolate third-party close hooks (e.g. the OG Assist SDK): a throw must
      // not abort the loop or leave other managed drawers open / the layout
      // state stale. Mirrors ShellEventBus.emit's per-handler try/catch.
      try {
        this.closeHooks.get(s.id)?.();
      } catch (err) {
        console.error(`[DrawerSlots] onRequestClose threw for "${s.id}":`, err);
      }
      changed = true;
      return { ...s, open: false };
    });
    if (changed) this.emit();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): readonly DrawerSlotRecord[] => this.slots;

  private emit(): void {
    for (const listener of this.listeners) listener();
  }

  private warnOnOrderCollision(record: DrawerSlotRecord): void {
    if (!import.meta.env.DEV) return;
    const collides = this.slots.some((s) => s.tier === record.tier && s.order === record.order);
    if (collides) {
      console.warn(
        `[DrawerSlots] Two ${record.tier} drawer slots requested order ${record.order}. ` +
          `Placement falls back to registration order — pass distinct \`order\` values for a stable layout.`
      );
    }
  }
}

/** App-wide singleton. Tests construct their own `new DrawerSlotStore()`. */
export const drawerSlotStore = new DrawerSlotStore();
